import { Bluetooth, Gamepad2, Play } from "lucide-react";
import { cn } from "@/lib/utils";
import { isFlipped, styleRoles, styleText } from "@/rc/drivetrains";
import { ATTACH_KINDS, BUTTON_SHORT, isTrigger } from "@/rc/attachments";
import { ControllerDiagram } from "@/components/rc/ControllerDiagram";
import { SpinDiagram } from "@/components/rc/SpinDiagram";

export const Tile = ({ title, children, testId, className }) => (
  <section className={cn("rounded-3xl border bg-card/95 p-5 backdrop-blur sm:p-6", className)} data-testid={testId}>
    <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</h2>
    <div className="mt-4">{children}</div>
  </section>
);

const PortChip = ({ port }) => (
  <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-primary font-display text-sm font-bold text-primary-foreground">{port}</span>
);

export const MotorList = ({ dt, cfg }) => (
  <ul className="space-y-2" data-testid="setup-motor-list">
    {dt.motors.map((m) => (
      <li key={m.id} className="flex items-center justify-between gap-3 text-sm" data-testid={`setup-motor-${m.id}`}>
        <span className="font-medium">{m.steer ? "Steering" : m.label}</span>
        <span className="flex items-center gap-2">
          {isFlipped(m, cfg.motors[m.id].dir) && <span className="text-xs font-semibold text-[hsl(var(--rc-hot))]">reversed</span>}
          <PortChip port={cfg.motors[m.id].port} />
        </span>
      </li>
    ))}
    {dt.kind === "car" && <li className="pt-1 text-xs text-muted-foreground" data-testid="setup-steer-max">Steers up to {cfg.steerMax}° each way</li>}
  </ul>
);

const Stick = ({ side, role }) => (
  <div className={cn("rounded-xl border px-3 py-2 text-xs", role ? "border-primary/40 bg-secondary text-secondary-foreground" : "border-dashed text-muted-foreground")}>
    <p className="font-semibold">{side}</p>
    <p>{role ? role.label : "Not used"}</p>
  </div>
);

export const ControlsInfo = ({ dt, cfg }) => {
  const roles = styleRoles(cfg.style, dt.kind);
  const t = styleText(cfg.style, dt.kind);
  return (
    <div className="space-y-4">
      <div className="rounded-2xl bg-muted/40 px-6 pb-3 pt-6"><ControllerDiagram roles={roles} className="mx-auto w-full max-w-[220px]" /></div>
      <div>
        <p className="font-display text-lg font-bold" data-testid="setup-style-name">{t.name}</p>
        <p className="text-sm text-muted-foreground">{t.desc}</p>
      </div>
      <div className="grid grid-cols-2 gap-2"><Stick side="Left stick" role={roles.left} /><Stick side="Right stick" role={roles.right} /></div>
      <p className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="setup-pairing">
        <Gamepad2 className="h-4 w-4" />Pairs with the first Xbox controller in pairing mode
      </p>
    </div>
  );
};

const Key = ({ label, button, tone }) => (
  <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2.5 py-1 text-xs">
    <span className={cn("font-semibold", tone === "pos" ? "text-primary" : "text-[hsl(var(--rc-hot))]")}>{label}</span>
    <b className="font-display">{BUTTON_SHORT[button]}</b>
  </span>
);

const AttachmentRow = ({ a, index }) => {
  const k = ATTACH_KINDS[a.kind] || ATTACH_KINDS.other;
  return (
    <li className="grid items-center gap-4 rounded-2xl border bg-background/60 p-4 sm:grid-cols-[150px_1fr]" data-testid={`setup-attachment-${index}`}>
      <SpinDiagram a={a} testId={`setup-attachment-${index}-spin`} />
      <div className="space-y-2">
        <p className="font-display text-lg font-bold">{k.name} <span className="text-sm font-normal text-muted-foreground">· {a.ports.length === 2 ? `motors ${a.ports.join(" + ")}, ${a.group === "same" ? "same way" : "opposite ways"}` : `motor ${a.ports[0]}`}</span></p>
        <div className="flex flex-wrap gap-2"><Key label={k.pos} button={a.pos} tone="pos" /><Key label={k.neg} button={a.neg} tone="neg" /></div>
        <p className="text-xs text-muted-foreground">
          Top speed {a.speed}%{[a.pos, a.neg].some(isTrigger) ? " (press triggers harder to go faster)" : ""} · {a.stop === "hold" ? "holds in place" : "coasts"} when you let go
        </p>
      </div>
    </li>
  );
};

export const AttachmentList = ({ attachments }) => (attachments.length ? (
  <ul className="grid gap-3 lg:grid-cols-2">{attachments.map((a, i) => <AttachmentRow key={i} a={a} index={i} />)}</ul>
) : (
  <p className="text-sm text-muted-foreground" data-testid="setup-no-attachments">No attachments. Add a claw, lift or arm with <b className="text-foreground">Edit setup</b>.</p>
));

const READY = [
  { icon: Bluetooth, t: "Connect your hub", d: "Press Connect hub and pick its name." },
  { icon: Play, t: "Press Drive", d: "HubPilot sends this setup to the hub and starts it." },
  { icon: Gamepad2, t: "Switch on the controller", d: "Then drive. You can close the laptop." },
];

export const ReadySteps = () => (
  <ol className="fade-up delay-3 grid gap-3 sm:grid-cols-3" data-testid="setup-ready-steps">
    {READY.map(({ icon: Icon, t, d }, i) => (
      <li key={t} className="flex gap-3 rounded-2xl border border-dashed bg-card/80 p-4">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-secondary text-secondary-foreground"><Icon className="h-4 w-4" /></span>
        <div>
          <p className="text-sm font-bold">{i + 1}. {t}</p>
          <p className="text-xs text-muted-foreground">{d}</p>
        </div>
      </li>
    ))}
  </ol>
);
